import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DocumentIcon,
  PaperClipIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { Button } from "../../components/button";
import { DeleteConfirm } from "../../components/modal/deleteConfirm";
import { Table } from "../../components/table";

const ACCEPT =
  ".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp,.heic,application/pdf,image/*";

const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Same empty state as the other site tabs. `compact` is a smaller version
// for use inside a form (the post sheet).
function EmptyFiles({ compact = false }) {
  return (
    <div
      className={`flex flex-col items-center justify-center ${
        compact ? "py-4" : "h-72"
      }`}
    >
      <DocumentIcon
        className={`${compact ? "h-6 w-6" : "h-10 w-10"} text-gray-300 dark:text-gray-600`}
      />
      <p
        className={`${compact ? "mt-2" : "mt-3"} text-sm text-gray-400 dark:text-gray-500 text-center`}
      >
        No files yet
      </p>
    </div>
  );
}

// Uploads one file to a site ({ siteId }) or a post ({ postId }).
async function uploadFile(authFetch, { siteId, postId }, file) {
  const formData = new FormData();
  formData.append(siteId ? "site_id" : "post_id", siteId || postId);
  formData.append("file", file);
  const response = await authFetch(
    `${process.env.REACT_APP_API_URL}/v1/files`,
    { method: "POST", body: formData }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Upload failed");
  }
}

// Files attached to a site or a post. Pass exactly one of siteId / postId.
// Links are short-lived signed URLs, so the list refetches when opened.
//
// With neither (e.g. a post that isn't created yet), files are held in
// `pendingFiles` and the parent uploads them once it has an id.
//
// variant "compact" (default) has its own heading and attach button, for use
// inside a form. variant "table" renders like the other site tabs: a table
// with an empty state, and the parent owns the button, calling
// ref.current.openPicker().
const Files = forwardRef(function Files(
  { siteId, postId, pendingFiles, onPendingChange, variant = "compact" },
  ref
) {
  const isPending = !siteId && !postId;
  const authFetch = useAuthFetch();
  const inputRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);

  const owner = siteId ? `site_id=${siteId}` : `post_id=${postId}`;

  const fetchDocs = useCallback(async () => {
    if (isPending) return;
    setIsLoading(true);
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/files?${owner}`
      );
      if (response.ok) setDocs(await response.json());
    } catch (err) {
      console.error("Error fetching files", err);
    } finally {
      setIsLoading(false);
    }
  }, [owner, authFetch, isPending]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    if (file.size > 20 * 1024 * 1024) {
      setError("File too large (max 20MB)");
      return;
    }
    if (isPending) {
      onPendingChange([...(pendingFiles || []), file]);
      return;
    }
    setIsUploading(true);
    try {
      await uploadFile(authFetch, { siteId, postId }, file);
      await fetchDocs();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openPicker: () => inputRef.current?.click(),
  }));

  const deleteDoc = async (id) => {
    const response = await authFetch(
      `${process.env.REACT_APP_API_URL}/v1/files/delete/${id}`,
      { method: "PUT" }
    );
    if (!response.ok) throw new Error("Failed to delete");
  };

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPT}
      className="hidden"
      onChange={handleFile}
    />
  );

  const deleteConfirm = (
    <DeleteConfirm
      isOpen={!!deleteItem}
      toggleOpen={() => setDeleteItem(null)}
      selectedDeleteItem={deleteItem}
      handleDeleteSuccess={fetchDocs}
      onDelete={deleteDoc}
    />
  );

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "file_name",
        Cell: ({ row }) => (
          <a
            href={row.original.url}
            target="_blank"
            rel="noreferrer"
            className="text-gray-800 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:underline"
          >
            {row.original.file_name}
          </a>
        ),
      },
      {
        Header: "Size",
        id: "size",
        accessor: (doc) => formatSize(doc.size_bytes),
      },
      {
        Header: "Added",
        id: "added",
        accessor: (doc) => new Date(doc.created_at).toLocaleDateString(),
      },
      {
        Header: "",
        id: "delete",
        accessor: () => "delete",
        Cell: ({ row }) => (
          <button
            type="button"
            onClick={() =>
              setDeleteItem({
                identifier: row.original.file_name,
                id: row.original.id,
              })
            }
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-150"
          >
            <TrashIcon className="h-4 w-4 shrink-0" />
          </button>
        ),
      },
    ],
    []
  );

  if (variant === "table") {
    return (
      <div>
        {fileInput}
        {(error || isUploading) && (
          <p
            className={`my-2 text-sm ${
              error ? "text-danger" : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {error || "Uploading..."}
          </p>
        )}
        <Table
          data={docs}
          columns={columns}
          isLoading={isLoading && docs.length === 0}
          emptyState={<EmptyFiles />}
        />
        {deleteConfirm}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="block text-sm font-medium text-gray-600 dark:text-gray-300">
          Files
        </span>
        <Button
          variant="secondary"
          size="sm"
          icon={PaperClipIcon}
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? "Uploading..." : "Attach file"}
        </Button>
        {fileInput}
      </div>
      {error && <p className="mb-2 text-sm text-danger">{error}</p>}
      {isPending ? (
        pendingFiles?.length ? (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {pendingFiles.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center justify-between py-2"
              >
                <span className="flex min-w-0 items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <DocumentIcon className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="truncate">{file.name}</span>
                  <span className="shrink-0 text-xs text-gray-400">
                    {formatSize(file.size)}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() =>
                    onPendingChange(pendingFiles.filter((_, i) => i !== index))
                  }
                  className="ml-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <TrashIcon className="h-4 w-4 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyFiles compact />
        )
      ) : docs.length === 0 ? (
        isLoading ? (
          <p className="py-4 text-sm text-gray-400 dark:text-gray-500">Loading...</p>
        ) : (
          <EmptyFiles compact />
        )
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {docs.map((doc) => (
            <li key={doc.id} className="flex items-center justify-between py-2">
              <a
                href={doc.url}
                target="_blank"
                rel="noreferrer"
                className="flex min-w-0 items-center gap-2 text-sm text-gray-700 dark:text-gray-200 hover:underline"
              >
                <DocumentIcon className="h-4 w-4 shrink-0 text-gray-400" />
                <span className="truncate">{doc.file_name}</span>
                <span className="shrink-0 text-xs text-gray-400">
                  {formatSize(doc.size_bytes)}
                </span>
              </a>
              <button
                type="button"
                onClick={() =>
                  setDeleteItem({ identifier: doc.file_name, id: doc.id })
                }
                className="ml-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <TrashIcon className="h-4 w-4 shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {deleteConfirm}
    </div>
  );
});

export { Files, uploadFile };
